import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DietaryTag {
  key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

export function useDietaryTags(includeInactive = false) {
  return useQuery({
    queryKey: ["dietary_tags", includeInactive],
    queryFn: async () => {
      let q = supabase.from("dietary_tags").select("*").order("sort_order");
      if (!includeInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return data as DietaryTag[];
    },
  });
}

export function useCreateDietaryTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tag: Omit<DietaryTag, "is_active"> & { is_active?: boolean }) => {
      const { data, error } = await supabase.from("dietary_tags").insert(tag).select().single();
      if (error) throw error;
      return data as DietaryTag;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dietary_tags"] }),
  });
}

export function useUpdateDietaryTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, ...updates }: Partial<DietaryTag> & { key: string }) => {
      const { error } = await supabase.from("dietary_tags").update(updates).eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dietary_tags"] }),
  });
}

export function useDeleteDietaryTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const { error } = await supabase.from("dietary_tags").delete().eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dietary_tags"] }),
  });
}

/** Helper to get label from dietary tags array */
export function getDietaryTagLabel(tags: DietaryTag[] | undefined, key: string): string {
  return tags?.find((t) => t.key === key)?.label ?? key;
}
